import {
    ActionPointInfo,
    IActionPointInfo,
    IMiddleware,
    IStopAction,
    Middleware,
    ServiceNames,
    StopAction,
    UIConfig,
    WorkbenchUIConfig,
} from '@tencent/workbench';
import { SLIDE_ADAPTER } from '../editor-adpater/slide-adapter';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { FIXED_KEYS_DOUBLE, FIXED_KEYS_SINGLE } from '../schema/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TOOLBAR_AND_MENU_REPORT_INFO, TOOLBAR_REPORT_MAP } from './report';
import { isLogin } from 'utils';

const getMenuManager = () => ((AppServiceProvider.getInstance()
    .getAppServices() as IPcEditorAppServices).editableServiceLayer?.menuManager);

/**
 * 返回通用的比如是否为查看态，是否打开历史记录等的置灰态
 */
export const getCommonDisabledState = (id: string) => {
    const pureInfoName = id.replace(/-workbench-[^ ]*/g, '');
    if (pureInfoName === 'divider') {
        return false;
    }
    const disableItems = getMenuManager()?.disableItemIds();
    if (!disableItems) return true;
    return disableItems.some(item => item.id === pureInfoName);
};

/**
 * 权限中间件
 * 按钮的公用隐藏状态
 */
@Middleware({
    adapters: [SLIDE_ADAPTER],
})
export class ToolbarHideMiddleware implements IMiddleware {
    public constructor() {
    }

    public use(@ActionPointInfo() info: IActionPointInfo) {
        if (info.name === 'divider') {
            return;
        }
    }
}

/**
 * disabled中间件
 * 根据disableItemIds确认按钮禁用状态
 */
@Middleware({
    adapters: [SLIDE_ADAPTER],
})
export class ToolbarDisabledMiddleware implements IMiddleware {
    public constructor() {
    }

    public use(@ActionPointInfo() info: IActionPointInfo) {
        // 表明按钮有shouldDisabled回调，回调会覆盖全局中间件的结果，所以此处无需执行
        if (info.hasRaw) {
            return;
        }
        if (info.path?.[0] === FIXED_KEYS_DOUBLE.TOOLBAR_VIP && !isLogin()) {
            return false;
        }
        return getCommonDisabledState(info.name);
    }
}

/**
 * check中间件
 * 禁用时，强制取消check态
 */
@Middleware({
    adapters: [SLIDE_ADAPTER],
})
export class ToolbarCheckedMiddleware implements IMiddleware {
    private isPcContainerRemoved = false;
    public constructor() {
    }

    public use(
    @ActionPointInfo() info: IActionPointInfo,
        @StopAction() stop: IStopAction,
        @UIConfig() config: WorkbenchUIConfig,
    ) {
        if (info.name === 'divider') {
            return;
        }

        /*
         * 撤销ssr加速的优化
         * 之前工具栏的ssr是跟着title bar加载完成后撤销的，不太合理，因为title bar有很多异步组件，撤销会很慢
         * 但这段代码放在这也是也不对的，wb应该提供一个首次完成收折计算/disable计算的钩子，用来给用户撤销ssr
         * 目前的$onReady钩子只能保证按钮渲染完，但是不能保证收折、中间件计算完
         * 跟踪这个issue -> https://git.woa.com/tencent-doc-core/workbench/issues/110
         * wb完成钩子开发后，将代码迁移到正确的地方，同时去掉setTimeout
         */
        if (!this.isPcContainerRemoved) {
            this.isPcContainerRemoved = true;
            const pcContainer = document.querySelector('.pre-page-header-tool') as HTMLDivElement;
            if (pcContainer) {
                requestAnimationFrame(() => {
                    pcContainer.remove();
                });
            }
        }

        const disableItems = getMenuManager()?.disableItemIds() || [];
        let checked = false;
        disableItems.forEach((item) => {
            if (item.id === info.name) {
                checked = false;
                // 如果按钮被禁用，则忽略后置的check态判断
                stop();
            }
        });
        return checked;
    }
}

const TOOL_TAB = 2;  // tab
const TOOL_MENU = 1; // menu

/**
 * 工具栏上报中间件
 */
@Middleware({
    adapters: [SLIDE_ADAPTER],
})
export class ToolbarReportMiddleware implements IMiddleware {
    public use(@ActionPointInfo() info: IActionPointInfo) {
        if (info.from !== ServiceNames.TOOLBAR) return;

        // 双栏的开始tab使用历史上报
        if (info.path.includes(FIXED_KEYS_DOUBLE.TOOLBAR_START)) return;
        // 单栏工具栏除了新增的菜单按钮外，均使用历史上报
        if (info.path.includes(FIXED_KEYS_SINGLE)) {
            if (info.path.includes(ActionType.SINGLE_TOOLBAR_MENU)) {
                tdw(TOOLBAR_REPORT_MAP[ActionType.SINGLE_TOOLBAR_MENU]);
            }
            return;
        }

        const pureInfoName = info.name.replace(/-workbench-[^ ]*/g, '');
        // 上报工具栏双栏新增TAB和menu相同功能
        if (TOOLBAR_AND_MENU_REPORT_INFO[pureInfoName]) {
            tdw({
                ...TOOLBAR_AND_MENU_REPORT_INFO[pureInfoName],
                ver3: TOOL_TAB,
            });
        } else if (TOOLBAR_REPORT_MAP[pureInfoName]) {
            // 上报工具栏其他数据
            tdw(TOOLBAR_REPORT_MAP[pureInfoName]());
        }
    }
}

/**
 * 下拉菜单上报中间件
 */
@Middleware({
    adapters: [SLIDE_ADAPTER],
})
export class MenuReportMiddleware implements IMiddleware {
    public use(@ActionPointInfo() info: IActionPointInfo) {
        if (info.from !== ServiceNames.MENU) return;

        const pureInfoName = info.name.replace(/-workbench-[^ ]*/g, '');
        // 上报工具栏双栏新增TAB和单栏菜单按钮相同功能
        if (TOOLBAR_AND_MENU_REPORT_INFO[pureInfoName]) {
            tdw({
                ...TOOLBAR_AND_MENU_REPORT_INFO[pureInfoName],
                ver3: TOOL_MENU,
            });
        }
    }
}
